import styled from 'styled-components';
import type { TextProps } from '../Text';
import { Text } from '../Text';
import { Breakpoints } from '@/styles/breakpoints';
import { useField, useFormContext } from 'remix-validated-form';
import Block from '@shared/components/ui/Block';

export const StyledInput = styled.input<{ $isError?: boolean }>`
    flex: 1;
    padding: 8px 12px;
    color: ${({ theme }) => theme.colors.uNavy};
    font-family: ${({ theme }) => theme.fonts.secondary.regular};
    background: ${({ theme }) => theme.colors.white};
    border-radius: 8px;
    border: solid 1px ${({ theme }) => theme.colors.uNavy20};
    appearance: none;

    &::placeholder {
        color: ${({ theme }) => theme.colors.mediumGrey};
    }

    ${({ $isError, theme }) =>
        $isError &&
        `
        box-shadow: 0px 0px 0px 2px ${theme.colors.red};
    `}

    &:focus {
        outline: none;
    }

    @media (min-width: ${Breakpoints.Medium}) {
        padding: 10px 16px;
    }
`;

export const StyledLabel = styled.label`
    display: flex;
    flex-direction: column;
`;

type InputProps = {
    label: string;
    name: string;
    hint?: string;
    labelTextProps?: Omit<TextProps, 'as'>;
    suffix?: React.ReactNode;
} & React.InputHTMLAttributes<HTMLInputElement>;

export const Input = ({
    label,
    name,
    hint,
    labelTextProps = { spacing: 'xxSmall' },
    suffix,
    ...props
}: InputProps) => {
    const { hasBeenSubmitted } = useFormContext();
    const { error, getInputProps } = useField(name);
    return (
        <StyledLabel>
            <Text {...labelTextProps} as="label" htmlFor={name} color="uNavy">
                {label}
            </Text>

            {hint && (
                <Text as={'span'} color="uNavy" size="small" spacing="xSmall">
                    {hint}
                </Text>
            )}

            <InputWrapper $stack="horizontal" $gap={8} $verticalAlign="bottom">
                <StyledInput
                    {...getInputProps({
                        id: name,
                        ...props,
                    })}
                    $isError={hasBeenSubmitted && !!error}
                />

                {suffix && (
                    <Text
                        as={'span'}
                        color="uNavy"
                        size="small"
                        weight="bold"
                        style={{ marginTop: '4px' }}
                    >
                        {suffix}
                    </Text>
                )}
            </InputWrapper>

            {hasBeenSubmitted && error && (
                <Error color="red" size="xSmall">
                    {error}
                </Error>
            )}
        </StyledLabel>
    );
};

const Error = styled(Text)`
    margin-top: ${({ theme }) => theme.spacing.xxSmall};
`;

export const InputWrapper = styled(Block)``;
